import is from '@sindresorhus/is';
import { type FunctionComponent, useEffect, useRef, useState } from 'react';

import { AssistantBubble } from '~/ui/domain/chat/AssistantBubble';
import { BundleCard } from '~/ui/domain/chat/BundleCard';
import { DiffCompareToggle } from '~/ui/domain/chat/DiffCompare';

import type { ChangeProposalBlock } from '../../data-contract/chat';
import { useChatStore } from '../stores/chat-open-store';
import { DiffBody } from './components/DiffBody';
import { DraftedToMatch } from './components/DraftedToMatch';
import { diffSupportsCompare, isEmptyDiff } from './components/diff-helpers';
import { QuestionnaireInputOverride } from './components/QuestionnaireInputOverride';
import { QuestionnaireThread } from './components/questionnaire-flow';
import type { BlockRenderer } from './types';

const ChangeProposalInline: FunctionComponent<{
    readonly block: ChangeProposalBlock;
}> = ({ block }) => {
    const [openCascade, setOpenCascade] = useState<Record<string, boolean>>({});
    const compareIds = useChatStore((state) => state.compareIds);
    const addCompareId = useChatStore((state) => state.addCompareId);
    const clearCompareIdsByPrefix = useChatStore((state) => state.clearCompareIdsByPrefix);
    const removeCompareId = useChatStore((state) => state.removeCompareId);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const pendingScrollKeyRef = useRef<string | null>(null);
    const sectionCompareId = (key: string): string => `${block.blockId}:${key}`;
    const isComparing = (key: string): boolean => compareIds.includes(sectionCompareId(key));
    const toggle = (key: string): void => {
        const willOpen = openCascade[key] !== true;
        if (willOpen) {
            pendingScrollKeyRef.current = key;
        } else if (isComparing(key)) {
            removeCompareId(sectionCompareId(key));
        }
        setOpenCascade((prev) => ({ ...prev, [key]: willOpen }));
    };
    const toggleCompare = (key: string): void => {
        const id = sectionCompareId(key);
        if (compareIds.includes(id)) {
            removeCompareId(id);
            return;
        }

        addCompareId(id);
    };

    useEffect(() => {
        const key = pendingScrollKeyRef.current;
        if (is.null(key) || openCascade[key] !== true) return;
        pendingScrollKeyRef.current = null;
        const frame = requestAnimationFrame(() => {
            sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return () => cancelAnimationFrame(frame);
    }, [openCascade]);

    useEffect(
        () => () => {
            clearCompareIdsByPrefix(`${block.blockId}:`);
        },
        [block.blockId, clearCompareIdsByPrefix],
    );

    const visibleDiffs = block.diffs.filter((diff) => !isEmptyDiff(diff));
    const [firstDiff] = visibleDiffs;
    if (is.undefined(firstDiff)) return null;
    const mainDiff = visibleDiffs.find((diff) => diff.isMain === true) ?? firstDiff;
    const cascadeDiffs = visibleDiffs.filter((diff) => diff !== mainDiff);

    return (
        <div className="flex flex-col gap-3">
            {is.nonEmptyString(block.title) ? (
                <AssistantBubble>
                    <AssistantBubble.Text fullWidth={true}>{block.title}</AssistantBubble.Text>
                </AssistantBubble>
            ) : null}
            <BundleCard
                lead={
                    <BundleCard.Section
                        label={mainDiff.title}
                        action={
                            diffSupportsCompare(mainDiff) ? (
                                <DiffCompareToggle
                                    active={isComparing('main')}
                                    onToggle={() => toggleCompare('main')}
                                />
                            ) : undefined
                        }
                    >
                        <DiffBody diff={mainDiff} compareActive={isComparing('main')} />
                    </BundleCard.Section>
                }
                bridge={undefined}
                cascade={
                    cascadeDiffs.length > 0 ? (
                        <div className="flex flex-col gap-m">
                            {cascadeDiffs.map((diff, index) => {
                                const key = `${diff.title}-${index}`;
                                return (
                                    <div
                                        key={key}
                                        ref={(node) => {
                                            sectionRefs.current[key] = node;
                                        }}
                                    >
                                        <BundleCard.Section
                                            label={diff.title}
                                            action={
                                                diffSupportsCompare(diff) ? (
                                                    <DiffCompareToggle
                                                        active={isComparing(key)}
                                                        onToggle={() => toggleCompare(key)}
                                                    />
                                                ) : undefined
                                            }
                                            status={<DraftedToMatch />}
                                            expanded={openCascade[key] === true}
                                            onToggle={() => toggle(key)}
                                        >
                                            <DiffBody diff={diff} compareActive={isComparing(key)} />
                                        </BundleCard.Section>
                                    </div>
                                );
                            })}
                        </div>
                    ) : undefined
                }
            />
            <QuestionnaireThread blockId={block.blockId} questions={[block.question]} />
        </div>
    );
};

export const changeProposalBlockRenderer: BlockRenderer<ChangeProposalBlock> = {
    isAnswered: (block) => block.mode === 'Result',
    renderInline: (block) => <ChangeProposalInline block={block} />,
    rendersInlineContent: (block) => block.diffs.some((diff) => !isEmptyDiff(diff)),
    renderInputOverride: (block, context) => {
        // While in Review the override self-gates: it renders nothing once the question
        // is answered (locally or echoed back), so the answer shows only in the thread.
        if (block.mode !== 'Review') return null;
        return (
            <QuestionnaireInputOverride
                key={block.blockId}
                blockId={block.blockId}
                eventId={context.eventId}
                questions={[block.question]}
            />
        );
    },
    type: 'change-proposal',
};
